// ============================================================================
// Hooks TanStack Query — RESULTATS FINANCIERS (E2 lecture + E2b ecriture)
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (`viewer` renvoye par l'API — jamais recalcule au callsite, lecon GA-05).
// Mutations E2b invalident ['gest','finance'] ET les indicateurs accueil
// (E1a consomme finance via /api/indicators/summary).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, buildQueryString } from '@gs/api-client';
import type { FinanceLineRow } from '../lib/finance/types';

export type GestFinanceRole = 'admin' | 'manager' | 'director' | 'employee';
export type FinanceView = 'visuels' | 'tableau';

interface Amounts {
  actual: number;
  budget: number;
  priorYear: number;
}

export interface FinanceSummary {
  totalRevenue: Amounts;
  totalExpense: Amounts;
  netIncome: Amounts;
  grossMargin: Amounts;
  lastUpdatedAt: string | null;
}

export interface FinanceTrend {
  periods: string[];
  revenue: number[];
  expense: number[];
  net: number[];
  margin: number[];
}

export interface FinanceYearPoint {
  period: string;
  isForecast: boolean;
  revenue: Amounts;
  expense: Amounts;
  net: Amounts;
}

export interface FinanceSummaryResponse {
  period: string;
  lines: FinanceLineRow[];
  summary: FinanceSummary;
  viewer: { role: GestFinanceRole; canViewDetail: boolean; canWrite: boolean };
}

export interface FinanceYearResponse {
  period: string;
  points: FinanceYearPoint[];
}

export interface FinanceDetailResponse {
  period: string;
  view: FinanceView;
  lines: FinanceLineRow[];
  summary: FinanceSummary;
  trend: FinanceTrend | null;
  viewer: { role: GestFinanceRole; canWrite: boolean };
}

export type ImportScenarioId = 'resultats' | 'budget' | 'budget-salarial';

export interface PeriodPreviewRow {
  period: string;
  revenue: number;
  expense: number;
  netComputed: number;
  revenueCsv: number | null;
  netCsv: number | null;
  deltaRevenue: number | null;
  deltaNet: number | null;
  ok: boolean;
}

export interface ImportPreview {
  periods: string[];
  rows: PeriodPreviewRow[];
  lineCount: number;
  warnings: number;
  hasColumnTypeRow: boolean;
}

export interface FinanceImportAnalyzeResponse {
  scenario: ImportScenarioId;
  writeKind: 'actuals' | 'budget';
  fileName: string | null;
  preview: ImportPreview;
  budgetWarning: string | null;
}

export interface FinanceImportCommitResponse {
  written: number;
  inserted: number;
  updated: number;
  reactivated: number;
  periods: string[];
  committedAt: string;
  scenario: ImportScenarioId;
}

export interface FinanceLinePutInput {
  period: string;
  section: 'revenue' | 'expense';
  code: string;
  amountActual: number;
  amountBudget: number;
  amountPriorYear: number;
  label?: string;
  sortOrder?: number;
  groupLabel?: string | null;
}

const KEY = ['gest', 'finance'] as const;
const ACCUEIL_INDICATORS_KEY = ['gest', 'accueil', 'indicators'] as const;

export function useFinanceSummary(period: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'summary', period ?? 'auto'] as const,
    queryFn: () => api.get<FinanceSummaryResponse>(`/api/finance/summary${buildQueryString({ period })}`),
    enabled,
  });
}

export function useFinanceYear(period: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'year', period ?? 'auto'] as const,
    queryFn: () => api.get<FinanceYearResponse>(`/api/finance/year${buildQueryString({ period })}`),
    enabled,
  });
}

export function useFinanceDetail(period: string | null, view: FinanceView, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'detail', period ?? 'auto', view] as const,
    queryFn: () => api.get<FinanceDetailResponse>(`/api/finance/detail${buildQueryString({ period, view })}`),
    enabled,
    retry: false,
  });
}

function useInvalidateFinance() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ACCUEIL_INDICATORS_KEY });
  };
}

/** E2b etape 1 : analyze (zero ecriture serveur). */
export function useFinanceImportAnalyze() {
  return useMutation({
    mutationFn: (body: { scenario: ImportScenarioId; csv: string; fileName?: string }) =>
      api.post<FinanceImportAnalyzeResponse>('/api/finance/import/analyze', body),
  });
}

/** E2b etape 2 : commit (re-parse + upsert transactionnel). */
export function useFinanceImportCommit() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (body: { scenario: ImportScenarioId; csv: string; fileName?: string }) =>
      api.post<FinanceImportCommitResponse>('/api/finance/import/commit', body),
    onSettled: invalidate,
  });
}

/** E2b saisie manuelle unitaire. */
export function useFinanceLinePut() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (body: FinanceLinePutInput) =>
      api.put<{ id: string; created: boolean; reactivated: boolean }>('/api/finance/line', body),
    onSettled: invalidate,
  });
}
